import type {
  AppUserId,
  AuthProviderUserId,
  StorageProviderName,
  SubmissionId,
  VideoId,
} from "./ids";

/**
 * Lifecycle of an uploaded recording.
 *
 * `pending_upload` is created *before* the browser touches object storage, so
 * that an abandoned upload leaves a record we can reconcile against rather
 * than an orphan object nobody knows about.
 */
export const VIDEO_STATUSES = [
  "pending_upload",
  "uploaded",
  "upload_failed",
  "delete_pending",
  "deleted",
] as const;
export type VideoStatus = (typeof VIDEO_STATUSES)[number];

/** How the file reached us. Affects nothing technically; useful for support. */
export const VIDEO_SOURCES = ["browser_recording", "file_upload"] as const;
export type VideoSource = (typeof VIDEO_SOURCES)[number];

export interface VideoRecord {
  readonly id: VideoId;
  readonly ownerId: AppUserId;
  readonly ownerAuthProviderUserId: AuthProviderUserId;
  readonly submissionId: SubmissionId | null;
  readonly status: VideoStatus;
  readonly source: VideoSource;
  readonly storageProvider: StorageProviderName;
  /** Bucket / container name. Never a public URL. */
  readonly bucket: string;
  /** Object key inside the bucket. Never a public URL. */
  readonly objectPath: string;
  /**
   * The MIME type the browser actually produced, as reported by
   * MediaRecorder. Not assumed to be WebM.
   */
  readonly mimeType: string;
  readonly sizeBytes: number | null;
  readonly durationMs: number | null;
  readonly createdAt: Date;
  readonly updatedAt: Date;
  readonly uploadCompletedAt: Date | null;
  readonly deleteAttempts: number;
  readonly lastDeleteError: string | null;
  readonly clientRequestId: string | null;
}

export interface VideoView {
  id: VideoId;
  submissionId: SubmissionId | null;
  status: VideoStatus;
  source: VideoSource;
  mimeType: string;
  sizeBytes: number | null;
  durationMs: number | null;
  createdAt: string;
  uploadCompletedAt: string | null;
  canDelete: boolean;
  canPlay: boolean;
}

export const toVideoView = (video: VideoRecord): VideoView => ({
  id: video.id,
  submissionId: video.submissionId,
  status: video.status,
  source: video.source,
  mimeType: video.mimeType,
  sizeBytes: video.sizeBytes,
  durationMs: video.durationMs,
  createdAt: video.createdAt.toISOString(),
  uploadCompletedAt: video.uploadCompletedAt
    ? video.uploadCompletedAt.toISOString()
    : null,
  canDelete: video.status !== "deleted" && video.status !== "delete_pending",
  canPlay: video.status === "uploaded",
});

/**
 * Upload instructions handed to the browser. The browser uploads straight to
 * object storage with these; large media never passes through the API.
 */
export interface UploadIntent {
  videoId: VideoId;
  storageProvider: StorageProviderName;
  bucket: string;
  objectPath: string;
  /** Server-enforced ceiling, mirrored in storage rules. */
  maxBytes: number;
  allowedMimeTypes: string[];
  /** Milliseconds; the client should stop recording at this point. */
  maxDurationMs: number;
  expiresAt: string;
}
